import React, { useEffect, useState, useContext } from "react";
import "../SiteSummary/SiteSummary.scss";
import Header from "../common/Header/Header";
import Spinner from "../common/Spinner/Spinner";
import { WellsContext } from "../../contexts/WellsContext";
import { useLocation, useNavigate } from "react-router-dom";
import AssetDetailsTabs from "../common/Tabs/AssetDetailsTab";
import SearchableSelect, { SelectOption } from "../common/SearchableSelect/SearchableSelect";

const assetDetailsTabs = [
  { label: "Status", content: <div>Content for Tab 1 goes here</div>, badge: true, Uri: "" },
  { label: "Runs", content: <div>Content for Tab 3 goes here</div>, badge: false, Uri: "" },
  { label: "Analysis", content: <div>Content for Tab 3 goes here</div>, badge: false, Uri: "" },
];

interface AssetdetailsProps {
  handleLogout: () => void;
}

/** Decode the stored JWT payload — never throws */
function getJwtPayload(): Record<string, any> | null {
  try {
    const token = localStorage.getItem("jwtAccessToken") || "";
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/** BFS: collect all well-descendants of a node from the flat hierarchy list */
function getDescendantWells(nodeId: string, data: any[]): SelectOption[] {
  const result: SelectOption[] = [];
  const queue = [String(nodeId)];
  while (queue.length) {
    const curr = queue.shift()!;
    for (const node of data) {
      if (String(node.parentId) === curr) {
        if (node.isWell) result.push({ id: String(node.id), name: node.label });
        else queue.push(String(node.id));
      }
    }
  }
  return result;
}

/**
 * DFS preorder: collect ALL non-well descendant groups of a node (at any depth).
 * Children appear immediately after their parent in the result, preserving tree order.
 * Nodes with wellCount === 0 are still explored (their children may have wells).
 */
function getAllDescendantGroups(nodeId: string, data: any[]): any[] {
  const result: any[] = [];
  function dfs(id: string) {
    for (const node of data) {
      if (String(node.parentId) === id && !node.isWell) {
        if (node.wellCount > 0) result.push(node);
        dfs(String(node.id));
      }
    }
  }
  dfs(String(nodeId));
  return result;
}

/** Build a SelectOption array with an "All" entry at the top */
function buildOptions(nodes: any[], allSecondaryLabel?: string): SelectOption[] {
  return [
    { id: "all", name: "All", secondaryLabel: allSecondaryLabel },
    ...nodes
      .filter((x) => x.wellCount > 0)
      .map((x) => ({ id: x.id, name: x.label, secondaryLabel: `${x.wellCount} wells` })),
  ];
}

const AssetDetails: React.FC<AssetdetailsProps> = ({ handleLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getAssetValues = () => {
    const params = new URLSearchParams(location.search);
    const qAssetId = params.get("assetId");
    if (!qAssetId) {
      return { assetName: null, assetId: null, customerId: null, currentTab: null, startTime: null, endTime: null, isInvalid: true };
    }
    return {
      assetName: location.state?.assetName || null,
      assetId: qAssetId,
      customerId: location.state?.customerId || null,
      currentTab: location.state?.currentTab || null,
      startTime: location.state?.startTime || null,
      endTime: location.state?.endTime || null,
      isInvalid: false,
    };
  };

  const { assetName, assetId, customerId, currentTab, startTime, endTime, isInvalid } = getAssetValues();

  const wellsCtx = useContext(WellsContext);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [stateCurrentTab, setStateCurrentTab] = useState<any>();
  const [selectedAssetname, setSelectedAssetname] = useState(assetName);
  const [selectedAssetId, setSelectedAssetId] = useState(assetId);
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId);

  const [hierarchyData, setHierarchyData] = useState<any[]>([]);

  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [regionOptions, setRegionOptions] = useState<SelectOption[]>([]);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [wellOptions, setWellOptions] = useState<SelectOption[]>([]);
  const [selectedWell, setSelectedWell] = useState(selectedAssetId || "");

  const hierarchyApi = `${process.env.REACT_APP_SERVER_ENDPOINT}/Wells/GetWellHierarchy`;

  const isValidWellId =
    assetId &&
    wellsCtx &&
    Array.isArray(wellsCtx.wells) &&
    wellsCtx.wells.some((w) => String(w.id) === String(assetId));

  const currentIndex = wellOptions.findIndex((x) => x.id === selectedWell);
  const isFirst = currentIndex <= 0;
  const isLast = currentIndex >= wellOptions.length - 1;

  const handleSelect = (options: any) => {
    setSelectedAssetId(options?.id);
    setSelectedCustomerId(options?.customerId);
    setSelectedAssetname(options?.name);
    if (options?.id) {
      const params = new URLSearchParams(location.search);
      params.set("assetId", String(options.id));
      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
        state: { assetId: options.id, assetName: options.name, customerId: options.customerId },
      });
    }
  };

  const navigateToWell = (well: SelectOption) => {
    setSelectedWell(well.id);
    const ctxWell = wellsCtx.getById(well.id);
    handleSelect({ id: well.id, name: well.name, customerId: ctxWell?.customerId ?? selectedCustomerId });
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const ctxWells = wellsCtx?.wells || [];
        const mapped = ctxWells.map((w: any) => ({ id: w.id, name: w.name, customerId: w.customerId, value: w.value }));
        const paramsQ = new URLSearchParams(window.location.search);
        const qAssetId = paramsQ.get("assetId");
        if (qAssetId) {
          const found = mapped.find((d) => String(d.id) === String(qAssetId));
          if (!found) {
            const ensured = await wellsCtx?.ensureAssetPresent(qAssetId);
            if (ensured) {
              setSelectedAssetId(ensured.id);
              setSelectedAssetname(ensured.name);
              setSelectedCustomerId(ensured.customerId);
              setSelectedWell(ensured.id);
              navigate(`${location.pathname}${location.search}`, {
                replace: true,
                state: { assetId: ensured.id, assetName: ensured.name, customerId: ensured.customerId },
              });
            }
          } else {
            setSelectedAssetId(found.id);
            setSelectedAssetname(found.name);
            setSelectedCustomerId(found.customerId);
            setSelectedWell(found.id);
            navigate(`${location.pathname}${location.search}`, {
              replace: true,
              state: { assetId: found.id, assetName: found.name, customerId: found.customerId },
            });
          }
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [assetId]);

  useEffect(() => {
    const loadHierarchy = async () => {
      try {
        const response = await fetch(hierarchyApi, {
          headers: { Authorization: `Bearer ${localStorage.getItem("jwtAccessToken")}` },
        });
        const data = await response.json();
        setHierarchyData(data);

        const jwtPayload = getJwtPayload();
        const adminFlag = jwtPayload?.GlobalAdmin === "Y";
        setIsGlobalAdmin(adminFlag);

        const rootNode = data.find((x: any) => x.id === "#");
        const rootChildren = data.filter((x: any) => x.parentId === "#" && !x.isWell);

        // Both Customer and Region always default to "All" on page load
        if (adminFlag) {
          setCustomerOptions(buildOptions(rootChildren, rootNode ? `${rootNode.wellCount} wells` : undefined));
          setSelectedCustomer("all");

          const custIds = rootChildren.map((c: any) => String(c.id));
          const regionNodes = custIds.flatMap((cId: string) => getAllDescendantGroups(cId, data));
          setRegionOptions(buildOptions(regionNodes, rootNode ? `${rootNode.wellCount} wells` : undefined));
          setSelectedRegion("all");

        } else {
          // For customer/user login the API may return one of three shapes:
          //   (a) # → CustomerWrapper → [Regions…]  (single customer node under #)
          //   (b) # → [Region1, Region2…]            (regions directly under #)
          //   (c) No # root at all — roots have parentId null / non-existent id
          const hasSubGroups = (id: string) =>
            data.some((x: any) => String(x.parentId) === id && !x.isWell);

          let scopeId: string = "#";
          let allLabel: string | undefined;

          if (rootChildren.length === 1 && hasSubGroups(String(rootChildren[0].id))) {
            scopeId = String(rootChildren[0].id);
            allLabel = rootChildren[0].wellCount ? `${rootChildren[0].wellCount} wells` : undefined;
          } else if (rootChildren.length > 0) {
            scopeId = "#";
            allLabel = rootNode ? `${rootNode.wellCount} wells` : undefined;
          } else {
            const allIds = new Set(data.map((x: any) => String(x.id)));
            const orphanRoots = data.filter(
              (x: any) => !x.isWell && (!x.parentId || !allIds.has(String(x.parentId)))
            );
            if (orphanRoots.length === 1 && hasSubGroups(String(orphanRoots[0].id))) {
              scopeId = String(orphanRoots[0].id);
              allLabel = orphanRoots[0].wellCount ? `${orphanRoots[0].wellCount} wells` : undefined;
            } else {
              const regionNodes: any[] = [];
              for (const root of orphanRoots) {
                if (root.wellCount > 0) regionNodes.push(root);
                regionNodes.push(...getAllDescendantGroups(String(root.id), data));
              }
              const totalWells = orphanRoots.reduce((s: number, n: any) => s + (n.wellCount || 0), 0);
              setRegionOptions(buildOptions(regionNodes, totalWells > 0 ? `${totalWells} wells` : undefined));
              setSelectedRegion("all");
              return;
            }
          }

          const regionNodes = getAllDescendantGroups(scopeId, data);
          setRegionOptions(buildOptions(regionNodes, allLabel));
          setSelectedRegion("all");
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadHierarchy();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isGlobalAdmin || hierarchyData.length === 0) return;

    const rootNode = hierarchyData.find((x: any) => x.id === "#");
    let regionNodes: any[];
    let allLabel: string | undefined;

    if (selectedCustomer === "all") {
      // Start inside each customer node — do NOT include the customer nodes themselves
      const custIds = hierarchyData
        .filter((x: any) => x.parentId === "#" && !x.isWell)
        .map((x: any) => x.id);
      regionNodes = custIds.flatMap((cId: string) => getAllDescendantGroups(cId, hierarchyData));
      allLabel = rootNode ? `${rootNode.wellCount} wells` : undefined;
    } else {
      const customer = hierarchyData.find((x: any) => x.id === selectedCustomer);
      regionNodes = getAllDescendantGroups(selectedCustomer, hierarchyData);
      allLabel = customer ? `${customer.wellCount} wells` : undefined;
    }

    setRegionOptions(buildOptions(regionNodes, allLabel));

    // Preserve the current region if it is still valid under the new customer selection;
    // only fall back to "all" when it no longer belongs to this customer's subtree.
    setSelectedRegion((prev) =>
      prev === "all" || regionNodes.some((r: any) => r.id === prev) ? prev : "all"
    );
  }, [selectedCustomer, hierarchyData, isGlobalAdmin]);

  useEffect(() => {
    if (hierarchyData.length === 0) return;

    if (selectedRegion !== "all") {
      setWellOptions(getDescendantWells(selectedRegion, hierarchyData));
    } else if (isGlobalAdmin && selectedCustomer !== "all") {
      setWellOptions(getDescendantWells(selectedCustomer, hierarchyData));
    } else {
      setWellOptions(
        hierarchyData
          .filter((x: any) => x.isWell)
          .map((x: any) => ({ id: x.id, name: x.label }))
      );
    }
  }, [selectedRegion, selectedCustomer, hierarchyData, isGlobalAdmin]);

  useEffect(() => {
    if (stateCurrentTab) setActiveTab(stateCurrentTab);
  }, [stateCurrentTab]);

  useEffect(() => {
    if (currentTab) {
      setActiveTab(currentTab);
      setStateCurrentTab(currentTab);
    }
  }, [currentTab]);

  useEffect(() => {
    if (selectedAssetId) setSelectedWell(selectedAssetId);
  }, [selectedAssetId]);

  if (wellsCtx.loading || loading) return <Spinner />;

  if (!isValidWellId || isInvalid) {
    return (
      <div className="flex items-center justify-center h-screen bg-darkcolor-800 text-white text-xl font-medium">
        Well Not Found with Asset ID, please provide valid Asset ID.
      </div>
    );
  }

  return (
    <div className="bg-darkcolor-800 content-wrapper">
      <>
        <Header
          home="Unify"
          selectedLabel={selectedAssetname || assetName || ""}
          loginCustomerName={localStorage.getItem("jwtUserName")}
          handleLogout={handleLogout}
        />

        {/* Filter bar: Customer (admin) | Region | Well | Prev/Next */}
        <div className="px-4 py-3 flex flex-wrap items-end gap-4 bg-darkcolor-800">

          {/* Dropdown 1 — Customer (Global Admin only) */}
          {isGlobalAdmin && (
            <div className="min-w-[220px]">
              <label className="block text-sm mb-1 text-neutral-25">Customer</label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomer}
                onChange={(id) => setSelectedCustomer(id)}
                placeholder="Select customer..."
              />
            </div>
          )}

          {/* Dropdown 2 — Region / Hierarchy */}
          <div className="min-w-[220px]">
            <label className="block text-sm mb-1 text-neutral-25">Region</label>
            <SearchableSelect
              options={regionOptions}
              value={selectedRegion}
              onChange={(id) => setSelectedRegion(id)}
              placeholder="Select region..."
              disabled={regionOptions.length === 0}
            />
          </div>

          {/* Dropdown 3 — Well */}
          <div className="min-w-[220px]">
            <label className="block text-sm mb-1 text-neutral-25">Well</label>
            <SearchableSelect
              options={wellOptions}
              value={selectedWell}
              onChange={(id) => {
                const well = wellOptions.find((x) => x.id === id);
                if (well) navigateToWell(well);
              }}
              placeholder="Select well..."
              disabled={wellOptions.length === 0}
            />
          </div>

          {/* Sequential navigation */}
          <div className="flex gap-2">
            <button
              disabled={isFirst}
              className="px-[14px] py-[8px] rounded-[8px] border-[1px] border-darkcolor-300 text-[14px] font-medium text-neutral-25 bg-darkcolor-800 disabled:opacity-40 hover:bg-darkcolor-600 transition-colors"
              onClick={() => {
                const prev = wellOptions[currentIndex - 1];
                if (prev) navigateToWell(prev);
              }}
            >
              ← Previous Well
            </button>
            <button
              disabled={isLast}
              className="px-[14px] py-[8px] rounded-[8px] border-[1px] border-darkcolor-300 text-[14px] font-medium text-neutral-25 bg-darkcolor-800 disabled:opacity-40 hover:bg-darkcolor-600 transition-colors"
              onClick={() => {
                const next = wellOptions[currentIndex + 1];
                if (next) navigateToWell(next);
              }}
            >
              Next Well →
            </button>
          </div>
        </div>

        <hr className="w-full h-[1px] mx-auto my-0 bg-darkcolor-300 border-0 rounded md:my-0 dark:bg-darkcolor-300" />

        <div className="flex bg-darkcolor-800 text-white">
          <div className="w-full mx-auto">
            <div className="flex flex-col items-center">
              <AssetDetailsTabs
                key={selectedAssetId}
                tabs={assetDetailsTabs}
                assetName={selectedAssetname}
                runTableData={[]}
                chartsData={[]}
                assetId={selectedAssetId}
                customerId={selectedCustomerId}
                handleLogout={handleLogout}
                index={activeTab}
                setStateCurrentTab={setStateCurrentTab}
                currentTab={currentTab}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
          </div>
        </div>
        <div className="mb-[100px]" />
      </>
    </div>
  );
};

export default AssetDetails;
